import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Divider, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Avatar, 
  Card, 
  CardContent, 
  Alert,
  Chip,
  Stack,
  SelectChangeEvent,
  Table,
  LinearProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';

import { useAuth } from '../../contexts/Auth';
import { authApi } from '../../services/authApi';
import { userApi, UserSession } from '../../services/userApi';

import 'dayjs/locale/tr';
import userGrants, { UserGrantsResponse, OfficeGrant, WarehouseGrant, SalesRegionGrant, SalespersonGrant } from '../../services/userGrants';

// Türkçe lokalizasyonu ayarla
dayjs.locale('tr');

const ProfilePage: React.FC = () => {
  const { 
    user, 
    sessionTimeoutMinutes, 
    setSessionTimeoutMinutes, 
    remainingSessionTime, 
    refreshSession,
    sessionSettings,
    setSessionSettings,
    userTimeZone,
    formatDateTimeForUser
  } = useAuth();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userSessions, setUserSessions] = useState<UserSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  // Canlı geri sayım için state
  const [liveRemainingTime, setLiveRemainingTime] = useState<number | null>(null);
  // Yetkiler
  const [myGrants, setMyGrants] = useState<UserGrantsResponse | null>(null);
  const [loadingGrants, setLoadingGrants] = useState(false);
  
  // Timezone ayarları için state'ler
  const [timezoneSettings, setTimezoneSettings] = useState({
    timeZone: '',
    useSystemTimeZone: true,
    availableTimeZones: [] as Array<{id: string, name: string, offset: string}>
  });
  const [loadingTimezone, setLoadingTimezone] = useState(false);
  
  // Timezone ayarlarını yükle
  const fetchTimezoneSettings = async () => {
    setLoadingTimezone(true);
    try {
      const settings = await authApi.getTimezoneSettings();
      setTimezoneSettings({
        timeZone: settings.timeZone || '',
        useSystemTimeZone: settings.useSystemTimeZone ?? true,
        availableTimeZones: settings.availableTimeZones || []
      });
    } catch (error) {
      console.error('Error fetching timezone settings:', error);
      setErrorMessage('Zaman dilimi ayarları yüklenirken bir hata oluştu');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setLoadingTimezone(false);
    }
  };

  // Kullanıcının yetkilerini getir
  const fetchMyGrants = async () => {
    setLoadingGrants(true);
    try {
      const grants = await userGrants.getMyGrants();
      setMyGrants(grants);
    } catch (error) {
      console.error('Yetkiler yüklenirken hata:', error);
      setErrorMessage('Yetkiler yüklenirken bir hata oluştu');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setLoadingGrants(false);
    }
  };

  // Timezone ayarlarını kaydet
  const handleTimezoneSettingsChange = async (newSettings: Partial<typeof timezoneSettings>) => {
    try {
      const updatedSettings = { ...timezoneSettings, ...newSettings };
      await authApi.saveTimezoneSettings({
        timeZone: updatedSettings.timeZone,
        useSystemTimeZone: updatedSettings.useSystemTimeZone
      });
      
      setTimezoneSettings(updatedSettings);
      
      // Session settings'i de güncelle
      await setSessionSettings({
        timeZone: updatedSettings.timeZone,
        useSystemTimeZone: updatedSettings.useSystemTimeZone
      });
      
      setSuccessMessage('Zaman dilimi ayarları başarıyla güncellendi');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error saving timezone settings:', error);
      setErrorMessage('Zaman dilimi ayarları kaydedilirken bir hata oluştu');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  // Sistem zaman dilimi kullanımını değiştir
  const handleUseSystemTimezoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const useSystemTimeZone = event.target.checked;
    handleTimezoneSettingsChange({ useSystemTimeZone });
  };

  // Zaman dilimi seçimini değiştir
  const handleTimezoneChange = (event: SelectChangeEvent<string>) => {
    const timeZone = event.target.value;
    handleTimezoneSettingsChange({ timeZone, useSystemTimeZone: false });
  };
  const handleSessionTimeoutChange = async (event: SelectChangeEvent<number>) => {
    const newTimeout = event.target.value as number;
    setSuccessMessage('Oturum süresi güncelleniyor...');
    
    try {
      await setSessionTimeoutMinutes(newTimeout);
      setSuccessMessage(`Oturum süresi ${newTimeout} dakika olarak güncellendi. Bu ayar tüm cihazlarınızda geçerli olacaktır.`);
      
      // Oturum süresini güncelledikten sonra kalan süreyi hemen güncelle
      if (remainingSessionTime) {
        setLiveRemainingTime(remainingSessionTime);
      }
    } catch (error) {
      console.error('Oturum süresi güncellenirken hata:', error);
      setErrorMessage('Oturum süresi güncellenirken bir hata oluştu');
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }
    
    // 5 saniye sonra mesajı kaldır
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  };

  // Otomatik oturum yenileme ayarını değiştirme
  const handleAutoRefreshChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const autoRefresh = event.target.checked;
    try {
      await setSessionSettings({ autoRefreshSession: autoRefresh });
      setSuccessMessage(
        autoRefresh 
          ? 'Otomatik oturum yenileme etkinleştirildi' 
          : 'Otomatik oturum yenileme devre dışı bırakıldı'
      );
      
      // 3 saniye sonra mesajı kaldır
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      setErrorMessage('Ayar güncellenirken bir hata oluştu');
      setTimeout(() => {
        setErrorMessage(null);
      }, 3000);
    }
  };
  
  // Oturumu yenileme işlemi
  const handleRefreshSession = async () => {
    try {
      await refreshSession();
      setSuccessMessage('Oturum başarıyla yenilendi');
      
      // 3 saniye sonra mesajı kaldır
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      setErrorMessage('Oturum yenileme sırasında bir hata oluştu');
      
      // 3 saniye sonra mesajı kaldır
      setTimeout(() => {
        setErrorMessage(null);
      }, 3000);
    }
  };
  
  // Kalan oturum süresini formatla
  const formatRemainingTime = () => {
    if (liveRemainingTime === null || liveRemainingTime === undefined) {
      // Eğer henüz hesaplanmamışsa, bilgilendirici bir mesaj göster
      return 'Yükleniyor...';
    }
    
    if (liveRemainingTime <= 0) {
      return 'Oturum süresi doldu';
    }
    
    const minutes = Math.floor(liveRemainingTime / 60);
    const seconds = liveRemainingTime % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Kullanıcı oturumlarını yükle
  const fetchUserSessions = async () => {
    if (!user) return;
    
    setLoadingSessions(true);
    try {
      const sessions = await userApi.getUserSessions();
      setUserSessions(sessions);
    } catch (error) {
      console.error('Error fetching user sessions:', error);
      setErrorMessage('Oturum geçmişi yüklenirken bir hata oluştu');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Oturum sonlandır
  const handleTerminateSession = async (sessionId: string) => {
    try {
      await userApi.terminateSession(sessionId);
      setSuccessMessage('Oturum başarıyla sonlandırıldı');
      setTimeout(() => setSuccessMessage(null), 3000);
      // Oturum listesini yenile
      await fetchUserSessions();
    } catch (error) {
      console.error('Error terminating session:', error);
      setErrorMessage('Oturum sonlandırılırken bir hata oluştu');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  // Tüm diğer oturumları sonlandır
  const handleTerminateAllSessions = async () => {
    try {
      await userApi.terminateAllSessions();
      setSuccessMessage('Tüm diğer oturumlar sonlandırıldı');
      setTimeout(() => setSuccessMessage(null), 3000);
      // Oturum listesini yenile
      await fetchUserSessions();
    } catch (error) {
      console.error('Error terminating all sessions:', error);
      setErrorMessage('Oturumlar sonlandırılırken bir hata oluştu');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  // Component yüklendiğinde oturumları ve timezone ayarlarını getir
  useEffect(() => {
    fetchUserSessions();
    fetchTimezoneSettings();
    fetchMyGrants();
  }, [user]);
  
  // Canlı geri sayım effect'i
  useEffect(() => {
    // remainingSessionTime değiştiğinde liveRemainingTime'i güncelle
    if (remainingSessionTime !== null && remainingSessionTime > 0) {
      setLiveRemainingTime(remainingSessionTime);
      console.log('ProfilePage: Updating remaining time:', remainingSessionTime, 'Session timeout:', sessionTimeoutMinutes);
    }
    
    // Eğer remainingSessionTime null veya geçersizse, interval'i kurma
    if (remainingSessionTime === null || remainingSessionTime <= 0) {
      return;
    }
    
    // Her saniye kalan süreyi 1 azalt
    const interval = setInterval(() => {
      setLiveRemainingTime(prevTime => {
        if (prevTime === null || prevTime <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    
    // Cleanup function
    return () => clearInterval(interval);
  }, [remainingSessionTime, sessionTimeoutMinutes]);
  
  // Oturum süresi değiştiğinde kullanıcıya bilgi ver
  useEffect(() => {
    if (sessionTimeoutMinutes) {
      console.log(`Oturum süresi ${sessionTimeoutMinutes} dakika olarak ayarlandı`);
    }
  }, [sessionTimeoutMinutes]);
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Profil Sayfası
      </Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      
      <Stack spacing={3}>
        {/* Kullanıcı Bilgileri ve Oturum Ayarları */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Kullanıcı Bilgileri */}
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="h6">Kullanıcı Bilgileri</Typography>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                <Box mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Ad Soyad
                  </Typography>
                  <Typography>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                </Box>
                
                <Box mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    E-posta
                  </Typography>
                  <Typography>{user?.email}</Typography>
                </Box>
                
                <Box mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Kullanıcı Adı
                  </Typography>
                  <Typography>{user?.userName}</Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Roller
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {user?.roles.map((role, index) => (
                      <Chip key={index} label={role} color="primary" size="small" />
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>

        {/* Yetkilerim */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                <SecurityIcon />
              </Avatar>
              <Typography variant="h6">Yetkilerim</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {loadingGrants ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                {/* Ofisler */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Ofisler
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {(myGrants?.offices || []).length === 0 ? (
                      <Typography color="text.secondary">Tanımlı ofis yetkisi yok</Typography>
                    ) : (
                      (myGrants?.offices || []).map((o: OfficeGrant, idx: number) => (
                        <Chip
                          key={`office-${idx}`}
                          label={`${o.officeCode || o.officeId}${o.officeName ? ' - ' + o.officeName : ''}`}
                          color={o.isDefault ? 'primary' : 'default'}
                          variant={o.isDefault ? 'filled' : 'outlined'}
                          icon={o.isDefault ? <CheckCircleIcon /> : undefined}
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                </Box>

                {/* Depolar */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Depolar
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {(myGrants?.warehouses || []).length === 0 ? (
                      <Typography color="text.secondary">Tanımlı depo yetkisi yok</Typography>
                    ) : (
                      (myGrants?.warehouses || []).map((w: WarehouseGrant, idx: number) => (
                        <Chip
                          key={`wh-${idx}`}
                          label={`${w.warehouseCode || w.warehouseId}${w.warehouseName ? ' - ' + w.warehouseName : ''}`}
                          color={w.isDefault ? 'primary' : 'default'}
                          variant={w.isDefault ? 'filled' : 'outlined'}
                          icon={w.isDefault ? <CheckCircleIcon /> : undefined}
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                </Box>

                {/* Satış Bölgeleri */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Satış Bölgeleri
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {(myGrants?.salesRegions || []).length === 0 ? (
                      <Typography color="text.secondary">Tanımlı satış bölgesi yok</Typography>
                    ) : (
                      (myGrants?.salesRegions || []).map((r: SalesRegionGrant, idx: number) => (
                        <Chip
                          key={`region-${idx}`}
                          label={r.regionName || r.stateCode || r.countryCode}
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                </Box>

                {/* Satış Sorumlusu */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Satış Sorumlusu
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {(myGrants?.salespersons || []).length === 0 ? (
                      <Typography color="text.secondary">Tanımlı satış sorumlusu yok</Typography>
                    ) : (
                      (myGrants?.salespersons || []).map((s: SalespersonGrant, idx: number) => (
                        <Chip
                          key={`sp-${idx}`}
                          label={`${s.salespersonCode}${s.salespersonName ? ' - ' + s.salespersonName : ''}`}
                          color={s.isDefault ? 'primary' : 'default'}
                          variant={s.isDefault ? 'filled' : 'outlined'}
                          icon={s.isDefault ? <CheckCircleIcon /> : undefined}
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>
          </Box>
          
          {/* Oturum Ayarları */}
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                    <AccessTimeIcon />
                  </Avatar>
                  <Typography variant="h6">Oturum Ayarları</Typography>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Oturum Süresi
                  </Typography>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Oturum Süresi</InputLabel>
                    <Select
                      value={sessionTimeoutMinutes}
                      onChange={handleSessionTimeoutChange}
                      label="Oturum Süresi"
                    >
                      <MenuItem value={5}>5 dakika</MenuItem>
                      <MenuItem value={10}>10 dakika</MenuItem>
                      <MenuItem value={15}>15 dakika</MenuItem>
                      <MenuItem value={20}>20 dakika</MenuItem>
                      <MenuItem value={30}>30 dakika</MenuItem>
                      <MenuItem value={45}>45 dakika</MenuItem>
                      <MenuItem value={60}>1 saat</MenuItem>
                      <MenuItem value={90}>1.5 saat</MenuItem>
                      <MenuItem value={120}>2 saat</MenuItem>
                      <MenuItem value={180}>3 saat</MenuItem>
                      <MenuItem value={240}>4 saat</MenuItem>
                      <MenuItem value={480}>8 saat</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Oturum süresi ayarı kullanıcı profilinize özeldir ve tüm cihazlarınızda geçerlidir.
                  </Typography>
                </Box>

                <Box mb={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sessionSettings.autoRefreshSession}
                        onChange={handleAutoRefreshChange}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Otomatik Oturum Yenileme
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Oturum sürenizin %75'i dolduğunda otomatik olarak yeniler
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Kalan Oturum Süresi
                  </Typography>
                  
                  {/* Büyük saat gösterimi */}
                  <Box display="flex" justifyContent="center" mb={2}>
                    <Typography 
                      variant="h3" 
                      fontWeight="medium" 
                      color={liveRemainingTime && liveRemainingTime < 60 ? "error" : "primary"}
                    >
                      {formatRemainingTime()}
                    </Typography>
                  </Box>
                  
                  {/* İlerleme çubuğu */}
                  <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={liveRemainingTime && sessionTimeoutMinutes ? 
                        (liveRemainingTime / (sessionTimeoutMinutes * 60)) * 100 : 0} 
                      color={liveRemainingTime && liveRemainingTime < 60 ? "error" : 
                             liveRemainingTime && liveRemainingTime < 300 ? "warning" : "primary"}
                      sx={{ height: 8, borderRadius: 2 }}
                    />
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center">
                      <AccessTimeIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {liveRemainingTime ? `${Math.floor(liveRemainingTime / 60)} dakika ${liveRemainingTime % 60} saniye kaldı` : ""}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Toplam süre: {sessionTimeoutMinutes} dakika
                    </Typography>
                  </Box>
                </Box>
                
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AccessTimeIcon />}
                  onClick={handleRefreshSession}
                  fullWidth
                >
                  Oturumu Yenile
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Stack>
        
        {/* Zaman Dilimi Ayarları */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                <PublicIcon />
              </Avatar>
              <Typography variant="h6">Zaman Dilimi Ayarları</Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {loadingTimezone ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={timezoneSettings.useSystemTimeZone}
                        onChange={handleUseSystemTimezoneChange}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Sistem Zaman Dilimini Kullan
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tarayıcınızın zaman dilimi ayarlarını kullanır
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                
                {!timezoneSettings.useSystemTimeZone && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Zaman Dilimi Seçin
                    </Typography>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Zaman Dilimi</InputLabel>
                      <Select
                        value={timezoneSettings.timeZone}
                        onChange={handleTimezoneChange}
                        label="Zaman Dilimi"
                      >
                        {timezoneSettings.availableTimeZones.map((tz) => (
                          <MenuItem key={tz.id} value={tz.id}>
                            {tz.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Mevcut Zaman Dilimi
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <PublicIcon color="action" sx={{ mr: 1 }} />
                    <Typography>
                      {(() => {
                        if (timezoneSettings.useSystemTimeZone) {
                          return userTimeZone || 'Sistem zaman dilimi';
                        } else {
                          const selectedTz = timezoneSettings.availableTimeZones.find(tz => tz.id === timezoneSettings.timeZone);
                          return selectedTz ? `${selectedTz.name} (${selectedTz.offset})` : timezoneSettings.timeZone || 'Bilinmiyor';
                        }
                      })()}
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Örnek Tarih/Saat Gösterimi
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <AccessTimeIcon color="action" sx={{ mr: 1 }} />
                    <Typography>
                      {formatDateTimeForUser ? formatDateTimeForUser(new Date()) : 'Bilinmiyor'}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>
        
        {/* Şifre Değiştirme */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                <SecurityIcon />
              </Avatar>
              <Typography variant="h6">Güvenlik Ayarları</Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Box component="form" noValidate>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Mevcut Şifre"
                  type="password"
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Yeni Şifre"
                  type="password"
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Yeni Şifre (Tekrar)"
                  type="password"
                  variant="outlined"
                />
              </Stack>
              
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                >
                  Şifreyi Değiştir
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        {/* Oturum Geçmişi */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <HistoryIcon />
                </Avatar>
                <Typography variant="h6">Oturum Geçmişi</Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleTerminateAllSessions}
              >
                Tüm Diğer Oturumları Sonlandır
              </Button>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {/* Oturum İstatistikleri */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
              <Card variant="outlined" sx={{ flex: 1, p: 2 }}>
                <Box display="flex" alignItems="center">
                  <HistoryIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{userSessions.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Oturum
                    </Typography>
                  </Box>
                </Box>
              </Card>
              
              <Card variant="outlined" sx={{ flex: 1, p: 2 }}>
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">
                      {userSessions.filter(s => s.isActive).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aktif Oturum
                    </Typography>
                  </Box>
                </Box>
              </Card>
              
              <Card variant="outlined" sx={{ flex: 1, p: 2 }}>
                <Box display="flex" alignItems="center">
                  <AccessTimeIcon color="secondary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">
                      {userSessions.length > 0 ? 
                        dayjs(userSessions[0]?.createdAt).format('DD.MM.YYYY') : 
                        'Bilinmiyor'
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Son Giriş
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Stack>
            
            {/* Oturum Tablosu */}
            {loadingSessions ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Giriş Tarihi</TableCell>
                      <TableCell>Son Aktivite</TableCell>
                      <TableCell>Cihaz</TableCell>
                      <TableCell>IP Adresi</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell align="center">İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userSessions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">
                            Oturum kaydı bulunamadı
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      userSessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell>
                            <Typography variant="body2">
                              {dayjs(session.createdAt).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {dayjs(session.lastActivity).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(() => {
                                const diffMinutes = dayjs().diff(dayjs(session.lastActivity), 'minute');
                                if (diffMinutes < 1) return 'Şimdi';
                                if (diffMinutes < 60) return `${diffMinutes} dakika önce`;
                                if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} saat önce`;
                                return `${Math.floor(diffMinutes / 1440)} gün önce`;
                              })()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <DevicesIcon 
                                fontSize="small" 
                                color={session.deviceInfo.toLowerCase().includes('mobile') ? 'primary' : 'action'}
                                sx={{ mr: 1 }} 
                              />
                              <Box>
                                <Typography variant="body2" noWrap>
                                  {session.deviceInfo.length > 20 ? 
                                    session.deviceInfo.substring(0, 20) + '...' : 
                                    session.deviceInfo
                                  }
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {session.deviceInfo.toLowerCase().includes('mobile') ? 'Mobil' : 'Masaüstü'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {session.ipAddress}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {session.isCurrent ? (
                              <Chip 
                                icon={<CheckCircleIcon />}
                                label="Mevcut Oturum" 
                                color="primary" 
                                size="small" 
                              />
                            ) : (
                              <Chip 
                                icon={session.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                                label={session.isActive ? 'Aktif' : (session.deactivatedAt ? 'Süresi Doldu' : 'Sonlandırıldı')} 
                                color={session.isActive ? 'success' : 'default'} 
                                size="small" 
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {session.isCurrent ? (
                              <Typography variant="caption" color="text.secondary">
                                Mevcut oturum
                              </Typography>
                            ) : session.isActive ? (
                              <Tooltip title="Oturumu Sonlandır">
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleTerminateSession(session.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Sonlandırıldı
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default ProfilePage;
